import { RenderStage, type FieldCache } from './stage';
import type { TextField } from '../text/textField';
import type { TextSettings } from '../text/settings';
import type { LoadedFont } from '../fonts/fontLoader';
import type { TechniqueContext } from '../../techniques/types';
import type { Layer } from '../../store/layer';
import type { StudioDocument } from '../../store/studioStore';

export interface RenderedDocument {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  width: number;
  height: number;
  field: TextField;
  textSettings: TextSettings;
  /** Per-layer simulation state, kept so the SVG exporter can read the same result. */
  states: Map<string, { layer: Layer; state: unknown; context: TechniqueContext }>;
  ms: number;
}

export interface RenderOptions {
  doc: StudioDocument;
  font: LoadedFont;
  /** Output multiplier. 1 matches the preview; 2–4 for print. */
  scale: number;
  /** Safety cap for techniques that never report completion. */
  maxSteps?: number;
  /** Called with 0–1 while the layers are computed. */
  onProgress?: (progress: number) => void;
  /** Shared across a sequence of renders so identical text geometry builds one SDF. */
  fieldCache?: FieldCache;
}

/**
 * Renders a document to a canvas at an arbitrary scale, independent of the preview.
 *
 * The export must not depend on how many frames the preview happened to render, so
 * every layer is stepped to completion here. Length-valued parameters are scaled by the
 * rule in `params/scale.ts`, and the text field is rebuilt at the output resolution —
 * so a 4× export is genuinely four times the detail, not an upscaled bitmap.
 *
 * The mechanics live in `RenderStage`, which the animation exporter drives frame by
 * frame; this function is the "run it all the way, then paint once" case.
 */
export function renderDocument(options: RenderOptions): RenderedDocument {
  const t0 = performance.now();
  const stage = new RenderStage({
    doc: options.doc,
    font: options.font,
    scale: options.scale,
    fieldCache: options.fieldCache,
  });

  stage.runToCompletion(options.maxSteps ?? 40000, options.onProgress);
  stage.paint();

  const states: RenderedDocument['states'] = new Map();
  for (const entry of stage.layers) {
    states.set(entry.layer.id, { layer: entry.layer, state: entry.state, context: entry.context });
  }

  return {
    canvas: stage.canvas,
    width: stage.width,
    height: stage.height,
    field: stage.field,
    textSettings: stage.textSettings,
    states,
    ms: performance.now() - t0,
  };
}
